import { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Loader2 } from 'lucide-react';
import { levelLabel, RESTRICTION_LABELS } from './conductUtils';

const LEVELS = ['LOGGED_WARNING', 'YELLOW', 'ORANGE', 'RED'];
const DAYS = [
  { v: 0, label: 'Mon' }, { v: 1, label: 'Tue' }, { v: 2, label: 'Wed' }, { v: 3, label: 'Thu' },
  { v: 4, label: 'Fri' }, { v: 5, label: 'Sat' }, { v: 6, label: 'Sun' },
];
const DEADLINE_FIELDS = [
  { key: 'statement_deadline_working_days', label: 'Employee statement deadline' },
  { key: 'ack_deadline_working_days', label: 'Employee acknowledgement deadline' },
  { key: 'appeal_window_working_days', label: 'Appeal window' },
  { key: 'red_reply_working_days', label: 'Red show-cause reply deadline' },
  { key: 'cap_draft_deadline_working_days', label: "Manager's CAP draft deadline" },
];
const TEMPLATE_FIELDS = [
  { key: 'letter_template_yellow', label: 'Yellow warning letter', placeholders: ['employee_name', 'staff_number', 'issued_date', 'notice_start_date', 'restrictions', 'review_dates'] },
  { key: 'letter_template_orange', label: 'Orange warning letter', placeholders: ['employee_name', 'staff_number', 'issued_date', 'notice_start_date', 'restrictions', 'review_dates'] },
  { key: 'letter_template_show_cause', label: 'Red show-cause notice', placeholders: ['employee_name', 'staff_number', 'issued_date', 'reply_days'] },
];

export default function SettingsTab({ isMd }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mdQuery, setMdQuery] = useState('');
  const [mdResults, setMdResults] = useState([]);
  const [newHoliday, setNewHoliday] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/conduct/settings');
      setSettings(res.data);
    } catch (e) { toast.error('Failed to load settings'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const searchMd = async (q) => {
    setMdQuery(q);
    if (q.length < 2) { setMdResults([]); return; }
    try { setMdResults((await api.get('/users/search', { params: { q } })).data); } catch { setMdResults([]); }
  };

  const update = (patch) => setSettings({ ...settings, ...patch });

  const isValid = () => {
    if (!Array.isArray(settings.working_days) || settings.working_days.length === 0) return false;
    for (const f of DEADLINE_FIELDS) {
      if (!Number.isInteger(settings[f.key]) || settings[f.key] < 1) return false;
    }
    for (const lvl of LEVELS) {
      const v = settings.clean_period_days_by_level?.[lvl];
      if (lvl !== 'RED' && (!Number.isInteger(v) || v < 1)) return false;
    }
    return true;
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/conduct/settings', settings);
      toast.success('Settings saved');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save settings');
    } finally { setSaving(false); }
  };

  if (loading || !settings) return <div className="flex justify-center py-12" data-testid="settings-loading"><Loader2 className="animate-spin" /></div>;

  const fieldProps = { disabled: !isMd };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="conduct-settings-tab">
      {!isMd && <p className="text-sm text-amber-700" data-testid="settings-view-only-notice">View only — only the MD can change these settings.</p>}

      <Card>
        <CardHeader><CardTitle className="text-base">Managing Director</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          <p className="text-sm" data-testid="settings-current-md">Current MD: <span className="font-medium">{settings.md_name || settings.md_user_id}</span></p>
          {isMd && (
            <>
              <Input placeholder="Search for a new MD by name or email" value={mdQuery} onChange={(e) => searchMd(e.target.value)} data-testid="settings-md-search-input" />
              {mdResults.length > 0 && (
                <div className="border rounded-sm divide-y max-h-36 overflow-y-auto">
                  {mdResults.map((r) => (
                    <button key={r.id} type="button" className="w-full text-left px-3 py-2 text-sm hover:bg-muted/60" data-testid={`settings-md-option-${r.id}`}
                      onClick={() => { update({ md_user_id: r.id, md_name: r.name }); setMdQuery(''); setMdResults([]); }}>
                      {r.name} <span className="text-muted-foreground">({r.email})</span>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Working Days</CardTitle></CardHeader>
        <CardContent className="flex flex-wrap gap-4">
          {DAYS.map((d) => (
            <label key={d.v} className="flex items-center gap-2 text-sm" data-testid={`settings-working-day-${d.label}`}>
              <Checkbox checked={(settings.working_days || []).includes(d.v)} disabled={!isMd}
                onCheckedChange={(checked) => {
                  const set = new Set(settings.working_days || []);
                  if (checked) set.add(d.v); else set.delete(d.v);
                  update({ working_days: Array.from(set).sort() });
                }} />
              {d.label}
            </label>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Holidays</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          <div className="flex flex-wrap gap-2">
            {(settings.holidays || []).map((h) => (
              <span key={h} className="text-xs border rounded-sm px-2 py-1 flex items-center gap-2" data-testid={`settings-holiday-${h}`}>
                {h}
                {isMd && (
                  <button type="button" className="text-muted-foreground hover:text-destructive" data-testid={`settings-remove-holiday-${h}`}
                    onClick={() => update({ holidays: (settings.holidays || []).filter((x) => x !== h) })}>×</button>
                )}
              </span>
            ))}
            {(settings.holidays || []).length === 0 && <p className="text-sm text-muted-foreground">No holidays set.</p>}
          </div>
          {isMd && (
            <div className="flex gap-2 items-center">
              <Input type="date" className="w-44" value={newHoliday} onChange={(e) => setNewHoliday(e.target.value)} data-testid="settings-new-holiday-input" />
              <Button size="sm" variant="outline" disabled={!newHoliday} data-testid="settings-add-holiday-btn"
                onClick={() => {
                  if (!(settings.holidays || []).includes(newHoliday)) update({ holidays: [...(settings.holidays || []), newHoliday].sort() });
                  setNewHoliday('');
                }}>Add Holiday</Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Deadlines (working days)</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 gap-4">
          {DEADLINE_FIELDS.map((f) => (
            <div key={f.key} className="space-y-1">
              <Label>{f.label}</Label>
              <Input type="number" min="1" value={settings[f.key] ?? ''} data-testid={`settings-${f.key}`} {...fieldProps}
                onChange={(e) => update({ [f.key]: e.target.value === '' ? '' : Number(e.target.value) })} />
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Review Days by Level</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 gap-4">
          {LEVELS.map((lvl) => (
            <div key={lvl} className="space-y-1">
              <Label>{levelLabel(lvl)} (comma-separated days, e.g. 15,30)</Label>
              <Input value={(settings.review_days_by_level?.[lvl] || []).join(',')} data-testid={`settings-review-days-${lvl}`} {...fieldProps}
                onChange={(e) => update({ review_days_by_level: { ...settings.review_days_by_level, [lvl]: e.target.value.split(',').map((s) => s.trim()).filter(Boolean).map(Number).filter((n) => Number.isInteger(n) && n >= 1) } })} />
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Clean Period (days) by Level</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 gap-4">
          {LEVELS.filter((l) => l !== 'RED').map((lvl) => (
            <div key={lvl} className="space-y-1">
              <Label>{levelLabel(lvl)}</Label>
              <Input type="number" min="1" value={settings.clean_period_days_by_level?.[lvl] ?? ''} data-testid={`settings-clean-period-${lvl}`} {...fieldProps}
                onChange={(e) => update({ clean_period_days_by_level: { ...settings.clean_period_days_by_level, [lvl]: e.target.value === '' ? '' : Number(e.target.value) } })} />
            </div>
          ))}
          <p className="text-xs text-muted-foreground col-span-2">Red cards don't clear on a timer — they're decided by the MD (Revert or Exit).</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Restrictions by Level</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {LEVELS.map((lvl) => (
            <div key={lvl}>
              <p className="text-sm font-medium mb-1">{levelLabel(lvl)}</p>
              <div className="flex flex-wrap gap-3">
                {Object.keys(RESTRICTION_LABELS).map((code) => (
                  <label key={code} className="flex items-center gap-1.5 text-xs" data-testid={`settings-restriction-${lvl}-${code}`}>
                    <Checkbox checked={(settings.restrictions_by_level?.[lvl] || []).includes(code)} disabled={!isMd}
                      onCheckedChange={(checked) => {
                        const set = new Set(settings.restrictions_by_level?.[lvl] || []);
                        if (checked) set.add(code); else set.delete(code);
                        update({ restrictions_by_level: { ...settings.restrictions_by_level, [lvl]: Array.from(set) } });
                      }} />
                    {RESTRICTION_LABELS[code]}
                  </label>
                ))}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Letter Templates</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {settings.staff_number_placeholder_note && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2" data-testid="settings-staff-number-note">
              {'{staff_number}'} is now available; add it to your edited letters.
            </p>
          )}
          {TEMPLATE_FIELDS.map((f) => (
            <div key={f.key} className="space-y-1">
              <Label>{f.label}</Label>
              <p className="text-xs text-muted-foreground">Allowed placeholders: {f.placeholders.map((p) => `{${p}}`).join(', ')}</p>
              <Textarea rows={5} className="text-xs font-mono" value={settings[f.key] || ''} data-testid={`settings-${f.key}`} {...fieldProps}
                onChange={(e) => update({ [f.key]: e.target.value })} />
            </div>
          ))}
        </CardContent>
      </Card>

      {isMd && (
        <div className="space-y-1">
          {!isValid() && <p className="text-xs text-destructive" data-testid="settings-invalid-notice">Fill in every deadline and clean period with a whole number of at least 1, and keep at least one working day checked, before saving.</p>}
          <Button data-testid="settings-save-btn" disabled={saving || !isValid()} onClick={save}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Save Settings
          </Button>
        </div>
      )}
    </div>
  );
}
